/**
 * Spinner - Primary loading indicator
 *
 * Rotating circular spinner using CSS conic gradient animation.
 * Theme-aware via CSS custom properties for action-primary color.
 *
 * WHY styled-component over component:
 * - Pure presentation component with no logic
 * - Animation handled via CSS keyframes
 * - Theme integration through CSS variables
 *
 * Accessibility:
 * - role="status" for screen readers
 * - aria-label provides context for loading state
 * - data-testid for testing hooks
 *
 * @see src/ui/SpinnerMini.tsx - Smaller variant
 * @see src/ui/SpinnerFullPage.tsx - Full page loading
 */
import styled, { keyframes } from 'styled-components';

/**
 * Rotation keyframe animation (360° turn)
 */
const rotate = keyframes`
  to {
    transform: rotate(1turn)
  }
`;

/**
 * Styled spinner with conic gradient and radial mask
 *
 * WHY complex gradient approach:
 * - Conic gradient creates spinning effect
 * - Radial gradient adds visual polish
 * - Webkit mask creates donut shape
 */
const SpinnerStyled = styled.div`
  margin: 4.8rem auto;
  z-index: 9999;
  position: relative;
  width: 6.4rem;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(
        farthest-side,
        var(--theme-action-primary) 94%,
        #0000
      )
      top/10px 10px no-repeat,
    conic-gradient(#0000 30%, var(--theme-action-primary));
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 10px), #000 0);
  animation: ${rotate} 1.5s infinite linear;
`;

/**
 * Spinner component
 *
 * WHY no props interface:
 * - Accepts any HTML div attributes via spread
 * - No custom props needed
 * - Flexibility for className, style, etc.
 */
const Spinner = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <SpinnerStyled data-testid="spinner" role="status" aria-label="Loading" {...props} />
);

export default Spinner;
